import React, { useContext, useEffect, useState } from "react";
import { View } from "react-native";
import { useSafeAreaFrame } from "react-native-safe-area-context";
import { useTheme } from "@emotion/react";
import BookmarkButton from "../../components/BookmarkButton";
import Text from "../../components/Text";
import StrategyListItem from "../../components/StrategyListItem";
import MarkdownText from "../../components/MarkdownText";
import RichText, { InlineRichText } from "../../components/RichText";
import createImageUrl from "../../utils/imageUrl";
import { useExternalState } from "../../utils/me";
import { FurnitureContext } from "../../utils/furniture";
import placeHolder from "../../resources/img/placeHolder.png";
import Gradient from "./components/Gradient";
import YouTubePlayer from "./components/YouTubePlayer";
import {
    Container,
    Background,
    Body,
    Header,
    Hero,
    Scroll,
    MediaImage,
} from "./styles";
import Snippet, { SnippetType } from "../../components/Snippet";
import {
    StrategyOrderContext,
    StrategyCategoryOrderContext,
} from "../../utils/strategy";
import { NavContext } from "../../utils/nav";
import { Button } from "../..";

const Strategy = ({ navigation, route }) => {
    const windowWidth = 800;
    const frame = useSafeAreaFrame();
    const [isBookmarked, setIsBookmarked] = useState(false);
    const [showMore, setShowMore] = useState(false);
    const { setLeafNav } = useContext(NavContext);
    const { strategyId } = route.params;
    const { available: all } = useContext(StrategyOrderContext);
    const { available: categories } = useContext(StrategyCategoryOrderContext);
    const furniture = useContext(FurnitureContext);
    const strategy = all.find(strategy => strategy._id === strategyId);
    const categoryId = strategy?._meta?.strategyCategoryId;
    const {
        bookmarkStates,
        api: { markContentAsRead, bookmarkContent, unBookmarkContent },
    } = useExternalState();
    const { backgroundPattern }: any = useTheme();

    useEffect(() => {
        setLeafNav(navigation);
    }, [navigation]);

    useEffect(() => {
        if (bookmarkStates) {
            const isBookmarked = !!bookmarkStates.find(
                bookmark => bookmark.contentId === strategyId,
            );
            setIsBookmarked(isBookmarked);
        }
    }, [bookmarkStates]);

    if (!strategy) {
        return <Container />;
    }

    const renderSnippet = ({
        type,
        title,
        titleRich,
        body,
        bodyRich,
        youTubeVideoId,
        youTubeStartTime,
        youTubeEndTime,
        media,
    }) => {
        let result = null;

        const theBody = bodyRich ? <RichText doc={bodyRich} /> : body;

        const theTitle = titleRich ? <InlineRichText doc={titleRich} /> : title;

        switch (type) {
            case SnippetType.Default: {
                let mediaContent;

                if (youTubeVideoId) {
                    mediaContent = (
                        <YouTubePlayer
                            youTubeVideoId={youTubeVideoId}
                            youTubeStartTime={youTubeStartTime}
                            youTubeEndTime={youTubeEndTime}
                        />
                    );
                    // mediaContent = null;
                } else if (media) {
                    const mediaUrl = createImageUrl(media);
                    mediaUrl.uri = `${mediaUrl.uri}?w=${windowWidth}&fm=jpg`;
                    mediaContent = (
                        <MediaImage
                            width={frame.width * 0.9}
                            source={mediaUrl}
                            testId="media-snippet-media-image"
                        />
                    );
                }

                result = (
                    <View style={{ marginHorizontal: 20, marginBottom: 36 }}>
                        <Text type="h10">{theTitle}</Text>
                        <Text type="body4">{theBody}</Text>
                        <View style={{ marginTop: 12 }}>{mediaContent}</View>
                    </View>
                );
            }
        }

        return result;
    };

    const {
        title,
        titleRich,
        body,
        bodyRich,
        image,
        mediaSnippet,
        resourceSnippet,
        more: moreRich,
    } = strategy;
    const img = createImageUrl(image, placeHolder);
    img.uri = `${img.uri}?w=${windowWidth}&fm=jpg`;
    // only display strategies in this category
    const { strategies: categoryStrategies } = categories.find(
        cat => cat._id === categoryId,
    );
    // get the current index of the current strategy so we can
    // display strategies which fall "after" the current strategy
    const strategyIndex: number = categoryStrategies.findIndex(
        strategy => strategy._id === strategyId,
    );
    // display the next available strategy first, then follow in order until end.
    // Restart at the beginning of the list
    const upNext = [].concat(
        categoryStrategies.slice(strategyIndex + 1),
        categoryStrategies.slice(0, strategyIndex),
    );

    const handlePressBookmark = async () => {
        if (isBookmarked) {
            unBookmarkContent(strategyId);
        } else {
            bookmarkContent(strategyId);
        }
        setIsBookmarked(!isBookmarked);
    };

    const theBody = bodyRich ? (
        <RichText doc={bodyRich} />
    ) : (
        <MarkdownText>{body}</MarkdownText>
    );

    const theMore = moreRich ? (
        <RichText doc={moreRich} />
    ) : (
        <MarkdownText>{body}</MarkdownText>
    );

    const theTitle = titleRich ? <InlineRichText doc={titleRich} /> : title;

    return (
        <Container>
            <Background source={backgroundPattern} />
            <BookmarkButton
                isBookmarked={isBookmarked}
                onPress={handlePressBookmark}
            />
            <Scroll>
                <Hero source={img} />
                <Header>
                    <Text type="h3">{theTitle}</Text>
                </Header>
                <Body>{theBody}</Body>
                {mediaSnippet && renderSnippet(mediaSnippet)}
                {resourceSnippet && (
                    <Snippet
                        type={SnippetType.Resource}
                        resource={resourceSnippet.resource}
                        resourceCtaType={resourceSnippet.resourceCtaType}
                        resourceCtaTitle={resourceSnippet.resourceCtaTitle}
                    />
                )}

                {moreRich && moreRich.content.length > 0 ? (
                    <View style={{ marginBottom: 40 }}>
                        <Button
                            title="Show More"
                            onPress={() => {
                                setShowMore(!showMore);
                            }}
                        />
                    </View>
                ) : null}
                {showMore === true ? <Body>{theMore}</Body> : null}
                {upNext.length > 0 && (
                    <View>
                        <Gradient width={frame.width} height={33} />
                        <Text
                            type="h1"
                            style={{
                                marginTop: 20,
                                marginLeft: 20,
                                marginBottom: 15,
                            }}>
                            {furniture.labelStrategyUpNext}
                        </Text>
                        {upNext.map(_strategy => (
                            <StrategyListItem
                                key={_strategy._id}
                                strategy={_strategy}
                                isNew={_strategy?._meta?.isNew}
                                onPress={() => {
                                    markContentAsRead(_strategy._id);
                                    navigation.replace("Strategy", {
                                        strategyId: _strategy._id,
                                    });
                                }}
                            />
                        ))}
                    </View>
                )}
            </Scroll>
        </Container>
    );
};

export default Strategy;
